// Global Types
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  ChampionshipDocumentStatus,
} from 'common.data';
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getFileUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  championshipDocumentStatusLabel,
  i18next,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
  publicBorder,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ChampionshipDocumentFields from '../../../fields/ChampionshipDocumentFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Enums
import {
  championshipDocumentStatusClass,
} from '../../../../enums';

// Types
type $Props = $Translate & {
  id: string;
  item: $Model<'Championship'>;
};

type $State = {
  data: Array<$Model<'ChampionshipDocument'>>;
  loading: boolean;
};

class ChampionshipDocumentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.ChampionshipId && data.modelName === 'ChampionshipDocument') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyChampionshipDocument({
          ChampionshipId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const ChampionshipId = item.id;

    const newItem: $ModelUpsertParams<'ChampionshipDocument'> = {
      ChampionshipId,
      Content: null,
      DocumentDate: new Date(),
      File: null,
      Name: '',
      Public: true,
      PublishDate: null,
      Status: ChampionshipDocumentStatus.pending,
      StatusContent: null,
    };

    return (
      <SectionHeader
        bottomBorder
        iconClass={'icon-docs'}
        rightButtons={(
          <FormDialog
            fields={(
              <ChampionshipDocumentFields item={newItem} />
            )}
            id={'create-championship-document-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'ChampionshipDocument'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertChampionshipDocument(params);
              },
            })}
            subTitle={t('New')}
            title={t('Championship document')}
          >
            <Button
              color={'info'}
              id={'create-championship-document-button'}
              size={'sm'}
              title={t('Add new document')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Championship documents')}
      />
    );
  }

  renderButtons(dataItem: $Model<'ChampionshipDocument'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={dataItem.File ? getFileUrl({
            cdnUrl: config.CDN_URL,
            file: dataItem.File,
          }) : getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaDocument,
            docType: docType.pdf,
            id,
            language: i18next.language as $Language,
          })}
          id={`championship-document-file-button-${id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <FormDialog
          fields={(
            <ChampionshipDocumentFields item={dataItem} />
          )}
          id={`update-championship-document-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'ChampionshipDocument'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertChampionshipDocument(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Championship document')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-championship-document-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-championship-document-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteChampionshipDocument(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Championship document')}
        >
          <Button
            color={'danger'}
            id={`delete-championship-document-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Document date')}</th>
            <th>{t('Publish date')}</th>
            <th>{t('Status')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'ChampionshipDocument'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Name}</td>
              <TdDate value={dataItem.DocumentDate} />
              <TdDate
                duration
                multiple
                value={dataItem.PublishDate}
              />
              <td>
                <span className={`badge rounded-pill ${championshipDocumentStatusClass[dataItem.Status]}`}>
                  {t(championshipDocumentStatusLabel[dataItem.Status])}
                </span>
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default ChampionshipDocumentsSectionView;
